// Rincón «Cerebros» y su pantalla en la pared (rehecho el 13-10 con Pau: «que entienda de un vistazo qué
// pruebas hay vivas»). Arriba, lo normal: la versión de hoy, qué cerebro contesta y lo que acaba de quedarse
// para todos. Debajo, una tarjeta por prueba viva: una barra del 100 % con qué parte de los fans va a cada
// lado (gris = lo normal), cuánto lleva y el Allow de Pau que la encendió. Las cerradas no salen: el backend
// solo manda las que tienen sus fans repartidos. Los nombres vienen del registro de pruebas de Calypso: aquí
// no se escribe ninguno. Sin botones: solo mirar.
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Aviso, CuerpoWidget, WIDGET } from '../ui';
import { dinero, edad, enteros100, numero, SIN_VALOR, ZONA_SALA } from './formato';
import type { CerebroSala, CerebrosSala, LadoSala, NumerosLadoSala, PruebaVivaSala } from './tipos';
import { Calculando, SinDatos } from './piezas';
import { calculandoPrimeraVez, useSalaRincon } from './useSalaRincon';

/** Colores de los cerebros (los tres primeros por orden alfabético de nombre; los demás, gris). El primero, violeta de
 *  serie y no el oro de la marca (04-11, visual.md #1: el oro solo para lo que pide actuar). */
const COLORES = [
  { fondo: 'bg-oficina-serie-2', borde: 'border-oficina-serie-2' },
  { fondo: 'bg-oficina-cerebro-azul', borde: 'border-oficina-cerebro-azul' },
  { fondo: 'bg-oficina-cerebro-rosa', borde: 'border-oficina-cerebro-rosa' },
];
const OTRO = {
  fondo: 'bg-oficina-esperando',
  borde: 'border-oficina-esperando',
};

/** El color sigue al cerebro, no a su puesto: se fija por nombre (orden alfabético estable). */
function colores(cerebros: CerebroSala[]): (nombre: string) => (typeof COLORES)[number] {
  const nombres = cerebros.map((c) => c.nombre).sort();
  return (nombre) => COLORES[nombres.indexOf(nombre)] ?? OTRO;
}

/** Lados de una prueba: lo normal en gris apagado; lo que se prueba, con la paleta de gráficas (validada
 * sobre «elevada», se distingue también con daltonismo). */
const NORMAL = 'bg-oficina-gasto-apagado';
const PROBADOS = ['bg-oficina-cerebro-azul', 'bg-oficina-cerebro-rosa', 'bg-oficina-serie-2', 'bg-oficina-serie-3'];
const colorLado = (lados: LadoSala[], l: LadoSala) =>
  l.normal ? NORMAL : (PROBADOS[lados.filter((x) => !x.normal).indexOf(l)] ?? 'bg-oficina-serie-5');

const pct = (x: number) => `${numero(x, x > 0 && x < 1 ? 1 : 0)} %`;
const fecha = (iso: string) =>
  new Date(iso).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: ZONA_SALA,
  });

/** «versión de hace 17 h», «… 1 día», «… 3 días», contado desde su última versión (el Allow que cambió sus ajustes o su
 * código). */
function lleva(desde: string, hasta: number): string {
  const h = Math.max(0, (hasta - Date.parse(desde)) / 3_600_000);
  if (h < 24) return t('salaControl.cerebros.llevaHoras', { n: Math.max(1, Math.round(h)) });
  const d = Math.floor(h / 24);
  return d === 1 ? t('salaControl.cerebros.llevaDia') : t('salaControl.cerebros.llevaDias', { n: d });
}

/** Barra del 100 %: el nombre y la cifra dentro solo si caben; si no, van en la leyenda de debajo. */
function Barra({
  partes,
  etiqueta,
  alto,
}: {
  partes: Array<{ clave: string; nombre: string; pct: number; fondo: string }>;
  etiqueta: string;
  alto: 'grande' | 'pequena';
}) {
  return (
    <span
      role="img"
      aria-label={`${etiqueta}: ${partes.map((p) => `${p.nombre} ${pct(p.pct)}`).join(', ')}`}
      className={cn('flex w-full overflow-hidden', alto === 'grande' ? 'h-8 gap-0.5 rounded-o-s' : 'h-2 gap-0.5 rounded')}
    >
      {partes.map((p) => (
        <span
          key={p.clave}
          title={`${p.nombre} ${pct(p.pct)}`}
          className={cn(
            'flex min-w-1 items-center overflow-hidden whitespace-nowrap text-o-sub font-semibold text-oficina-texto',
            p.fondo,
            alto === 'grande' && 'px-2.5',
          )}
          style={{ width: `${p.pct}%` }}
        >
          {/* 10-10 (Pau): en una barra estrecha se cortaba el final y se perdía el «%»: se acorta el nombre, nunca la cifra. */}
          {alto === 'grande' && p.pct >= 15 ? (
            <>
              <span className="min-w-0 truncate">{p.nombre}</span>
              <span className="ml-1.5 shrink-0">{pct(p.pct)}</span>
            </>
          ) : null}
        </span>
      ))}
    </span>
  );
}

type Numero = keyof NumerosLadoSala;
const FILAS: Numero[] = ['coste_mil', 'cache_pct', 'venta_fan', 'bloqueos_mil'];
const valor = (k: Numero, x: number | null) =>
  x === null ? SIN_VALOR : k === 'cache_pct' ? `${numero(x, 1)} %` : k === 'bloqueos_mil' ? numero(x, 1) : dinero(x, 2);
const signo = (x: number) => (x > 0 ? '+' : x < 0 ? '−' : '±');

/** Frente al normal: «−4 %», «+0,8 puntos», «+12 % · aún puede ser azar». */
function frente(l: LadoSala, k: Numero): string | null {
  const f = l.frente;
  if (!f) return null;
  if (k === 'cache_pct') {
    if (f.cache_puntos === null) return null;
    const p = Math.round(f.cache_puntos * 10) / 10;
    return t('salaControl.cerebros.puntos', { signo: signo(p), n: numero(Math.abs(p), 1) });
  }
  const d0 = f[k];
  if (d0 === null) return null;
  const d = Math.round(d0); // «−0 %» no: lo que redondea a 0 sale «±0 %»
  const pc = `${signo(d)}${numero(Math.abs(d), 0)} %`;
  return k === 'venta_fan' ? `${pc} · ${t(f.venta_firme ? 'salaControl.cerebros.firme' : 'salaControl.cerebros.noFirme')}` : pc;
}

/** Los mismos cuatro números para toda prueba, un lado por columna (el normal primero). */
function Numeros({ p, ahora }: { p: PruebaVivaSala; ahora: number }) {
  if (p.numeros_en_min > 0) return <p className="text-o-nota text-oficina-texto3">{t('salaControl.cerebros.numerosEn', { n: p.numeros_en_min })}</p>;
  // Una prueba con versión de hoy se mide desde ella (01-11, Pau: «cada prueba cuente desde su última versión y la enseñe»).
  const titulo = p.numeros_desde
    ? t('salaControl.cerebros.numerosVersion', {
        fecha: fecha(p.numeros_desde),
        h: Math.max(1, Math.round((ahora - Date.parse(p.numeros_desde)) / 3_600_000)),
      })
    : t('salaControl.cerebros.numeros');
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-o-sub">
        <caption className="pb-1 text-left text-o-nota text-oficina-texto3">{titulo}</caption>
        <thead>
          <tr className="text-oficina-texto3">
            <th scope="col" className="py-1 pr-3 text-left font-normal" />
            {p.lados.map((l) => (
              <th key={l.clave} scope="col" className="py-1 pr-3 text-right font-semibold text-oficina-texto2">
                {l.nombre}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {FILAS.map((k) => (
            <tr key={k} className="border-t border-oficina-linea">
              <th scope="row" className="py-1 pr-3 text-left font-normal text-oficina-texto2">
                {t(`salaControl.cerebros.fila.${k}`)}
              </th>
              {p.lados.map((l) => {
                const f = frente(l, k);
                return (
                  <td key={l.clave} className="whitespace-nowrap py-1 pr-3 text-right">
                    <b className="font-semibold">{valor(k, l.numeros[k])}</b>
                    {f ? <span className={cn('block text-o-nota', k === 'venta_fan' && l.frente?.venta_firme ? 'font-semibold text-oficina-texto' : 'text-oficina-texto3')}>{f}</span> : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Chip({ titulo, valor, nota }: { titulo: string; valor: string; nota?: string }) {
  return (
    <div className="rounded-o-m bg-oficina-elevada px-3.5 py-2.5">
      <p className="text-o-nota text-oficina-texto3">{titulo}</p>
      <p className="text-o-cuerpo font-semibold">{valor}</p>
      {nota ? <p className="text-o-nota text-oficina-texto2">{nota}</p> : null}
    </div>
  );
}

function Prueba({ p: cruda, allowsDesde, ahora }: { p: PruebaVivaSala; allowsDesde: string | null; ahora: number }) {
  // Un lado por debajo del 1 % se queda con su decimal (no «0 %»); los demás, enteros que suman 100.
  const redondos = enteros100(cruda.lados.map((l) => l.pct));
  const p = { ...cruda, lados: cruda.lados.map((l, i) => (l.pct < 1 ? l : { ...l, pct: redondos[i]! })) };
  const origen = p.allow
    ? t('salaControl.cerebros.allow', { fecha: fecha(p.allow.en), titulo: p.allow.titulo })
    : allowsDesde
      ? t('salaControl.cerebros.sinAllow', { fecha: fecha(allowsDesde) })
      : t('salaControl.cerebros.sinAllowNunca');
  return (
    <li className="flex flex-col gap-2 rounded-o-m bg-oficina-elevada px-4 py-3">
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <h4 className="text-o-cuerpo font-semibold">
            {p.nombre}
            {p.sin_nombre ? <span className="text-o-nota font-normal text-oficina-bloqueado"> · {t('salaControl.cerebros.sinNombre')}</span> : null}
          </h4>
          {p.allow ? <span className="shrink-0 whitespace-nowrap text-o-nota text-oficina-texto2">{lleva(p.allow.en, ahora)}</span> : null}
        </div>
        <p className="text-o-sub text-oficina-texto2">
          {p.que_cambia}
          {p.solo_a ? <span className="text-oficina-te-necesita-claro"> {t('salaControl.cerebros.soloA', { quienes: p.solo_a })}</span> : null}
        </p>
        {p.busca ? <p className="text-o-sub text-oficina-texto">{t('salaControl.cerebros.busca', { texto: p.busca })}</p> : null}
      </div>
      <Barra
        alto="grande"
        etiqueta={t('salaControl.cerebros.barraPrueba', { nombre: p.nombre })}
        partes={p.lados.map((l) => ({ clave: l.clave, nombre: l.nombre, pct: l.pct, fondo: colorLado(p.lados, l) }))}
      />
      <ul className="flex flex-col gap-1 text-o-sub">
        {p.lados.map((l) => (
          <li key={l.clave} className="flex items-start gap-2">
            <i className={cn('mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-sm', colorLado(p.lados, l))} />
            <span className="min-w-0 flex-1">
              <b className="font-semibold">{l.nombre}</b>
              {l.normal ? <span className="text-oficina-texto3"> · {t('salaControl.cerebros.loNormal')}</span> : null}
              {l.sin_nombre && !l.normal ? <span className="text-oficina-bloqueado"> · {t('salaControl.cerebros.sinNombre')}</span> : null}
              {l.recibe ? <span className="block text-o-nota text-oficina-texto2">{l.recibe}</span> : null}
            </span>
            <b className="shrink-0 whitespace-nowrap font-semibold">{pct(l.pct)}</b>
          </li>
        ))}
      </ul>
      <Numeros p={p} ahora={ahora} />
      <p className="text-o-nota text-oficina-texto3">{origen}</p>
    </li>
  );
}

/** Edad de los datos: desde la última respuesta copiada (llegan con retraso), no desde el cálculo. */
const edadDe = (hasta: string | null | undefined) => (hasta ? Math.max(0, (Date.now() - Date.parse(hasta)) / 1000) : null);
const edadDatos = (d: CerebrosSala) => edadDe(d.datos_hasta);
/** Más que esto y la edad del cerebro sale en rojo: Calypso copia sus respuestas cada ~3 min, así que algo va mal. */
const CEREBRO_TARDE_S = 10 * 60;

export function Cerebros() {
  const { foto, fallo } = useSalaRincon<CerebrosSala>('cerebros');
  if (fallo && !foto) return <Aviso tono="error">{t('salaControl.errorCarga')}</Aviso>;
  if (calculandoPrimeraVez(foto)) return <Calculando />;
  if (!foto) return null;
  const d = foto.datos;
  if (!d || (d.fans === 0 && d.cerebros.length === 0)) {
    return foto.error ? (
      <Aviso tono="error">
        {t('salaControl.rincones.sinDatosPorError', {
          motivo: t(`salaControl.rincones.errores.${foto.error.mensaje}`),
        })}
      </Aviso>
    ) : (
      <SinDatos texto={t('salaControl.cerebros.sinDatos')} />
    );
  }
  const e = edadDatos(d);
  const color = colores(d.cerebros);
  const ahora = Date.now();
  return (
    <div className={cn('flex flex-col gap-4', (foto.vieja || foto.error) && 'opacity-60')}>
      <div>
        <h3 className="text-o-titulo3 font-semibold">{t('salaControl.cerebros.titulo')}</h3>
        {e !== null ? <p className="text-o-sub text-oficina-texto2">{t('salaControl.cerebros.sub', { fans: numero(d.fans), edad: edad(e) })}</p> : null}
      </div>
      <section className="flex flex-col gap-2">
        <p className="text-o-nota text-oficina-texto3">
          <b className="font-semibold">{t('salaControl.cerebros.normal')}</b> · {t('salaControl.cerebros.normalSub')}
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {d.version ? <Chip titulo={t('salaControl.cerebros.version')} valor={d.version} nota={t('salaControl.cerebros.versionHoy')} /> : null}
          <div className="rounded-o-m bg-oficina-elevada px-3.5 py-2.5">
            <p className="text-o-nota text-oficina-texto3">{t('salaControl.cerebros.cerebro')}</p>
            <p className="text-o-cuerpo font-semibold">
              {d.cerebros.map((c) => `${c.nombre} ${pct(c.pct)}${c.respaldo ? ` ${t('salaControl.cerebros.deRespaldo')}` : ''}`).join(' · ')}
            </p>
            {/* Fans por cerebro (01-11, Pau: «que cuente fans por cerebro, como las tarjetas»), en la misma media hora que
                las tarjetas; siempre con su edad, para que se vea si va tarde (15-10). */}
            {(() => {
              const ec = edadDe(d.cerebros_hasta);
              return ec !== null ? (
                <p className={cn('text-o-nota', ec > CEREBRO_TARDE_S ? 'text-oficina-error-claro' : 'text-oficina-texto3')}>
                  {t('salaControl.cerebros.cerebroEdad', { fans: numero(d.fans_cerebro ?? 0), min: d.cerebro_min ?? 30, edad: edad(ec) })}
                </p>
              ) : null;
            })()}
            {d.respaldo_pct ? <p className="text-o-nota text-oficina-texto3">{t('salaControl.cerebros.respaldo', { pct: pct(d.respaldo_pct) })}</p> : null}
            {d.cerebros.length > 1 ? (
              <span className="mt-1.5 block">
                <Barra
                  alto="pequena"
                  etiqueta={t('salaControl.cerebros.barra')}
                  partes={d.cerebros.map((c) => ({ clave: c.nombre, nombre: c.nombre, pct: c.pct, fondo: color(c.nombre).fondo }))}
                />
              </span>
            ) : null}
          </div>
          {d.ya_para_todos.map((y) => (
            <Chip key={y.id} titulo={t('salaControl.cerebros.yaParaTodos')} valor={y.nombre} nota={t('salaControl.cerebros.paraTodos', { fecha: fecha(y.allow.en) })} />
          ))}
        </div>
      </section>
      <section className="flex flex-col gap-2">
        <p className="text-o-nota font-semibold text-oficina-texto3">{t('salaControl.cerebros.vivas', { n: d.pruebas.length })}</p>
        {/* Respuestas sin lados apuntados: Calypso no está marcando las pruebas (versión vieja o roto). No es «no hay
            pruebas»: decirlo así escondería justo el fallo que hace que esta pestaña se quede vieja. */}
        {d.fans === 0 && d.respuestas > 0 ? (
          <Aviso tono="error">{t('salaControl.cerebros.sinLados')}</Aviso>
        ) : d.pruebas.length === 0 ? (
          <p className="text-o-sub text-oficina-texto2">{t('salaControl.cerebros.ninguna')}</p>
        ) : null}
        <ul className="flex flex-col gap-2">
          {d.pruebas.map((p) => (
            <Prueba key={p.id} p={p} allowsDesde={d.allows_desde} ahora={ahora} />
          ))}
        </ul>
        <p className="text-o-nota text-oficina-texto3">{t('salaControl.cerebros.nota')}</p>
      </section>
    </div>
  );
}

/** La pantalla pequeña en la pared, al lado de la de ventas: el cerebro que más fans lleva como cifra, los demás en su
 *  línea y la barra del reparto (Pau quiere conservarla en la pared, 04-11). */
export function PantallaCerebros({ alAbrir }: { alAbrir: () => void }) {
  const { foto } = useSalaRincon<CerebrosSala>('cerebros', 2 * 60_000);
  const cerebros = foto?.datos?.cerebros ?? [];
  const color = colores(cerebros);
  const resumen = cerebros.map((c) => `${c.nombre} ${pct(c.pct)}`).join(' · ');
  const [primero, ...demas] = [...cerebros].sort((a, b) => b.pct - a.pct);
  return (
    <button
      type="button"
      onClick={alAbrir}
      aria-label={`${t('salaControl.cerebros.titulo')}${resumen ? `: ${resumen}` : ''}`}
      className={WIDGET}
    >
      <CuerpoWidget
        punto={cerebros.length ? 'bien' : 'sinDatos'}
        titulo={t('salaControl.cerebros.pantalla')}
        linea={demas.map((c) => `${c.nombre} ${pct(c.pct)}`).join(' · ')}
        cifra={primero ? primero.nombre : SIN_VALOR}
        unidad={primero ? pct(primero.pct) : null}
        grafico={
          cerebros.length ? (
            <Barra
              alto="pequena"
              etiqueta={t('salaControl.cerebros.barra')}
              partes={cerebros.map((c) => ({ clave: c.nombre, nombre: c.nombre, pct: c.pct, fondo: color(c.nombre).fondo }))}
            />
          ) : null
        }
      />
    </button>
  );
}
